import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonContextManagers = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Context Managers: Safe Resource Handling</h1>
                <p className="subtitle">The with statement, resource lifecycles, and the mechanics of __enter__ and __exit__. Guaranteed cleanup.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine you are renting a <strong>Party Hall</strong> (a Resource like a File or Database Connection).
                        <strong>The Old Way:</strong> You pick up the key (`open()`), throw the party, and then you <em>must</em> remember to return the key (`close()`). If you party too hard (Exception) and forget, the hall stays locked forever. No one else can use it.
                        <strong>The "With" Way:</strong> You hire a <strong>Butler</strong> (Context Manager). The Butler unlocks the door for you. You party. When you leaveor even if you are carried out by security (Exception)the Butler automatically locks the door and turns off the lights.
                    </p>
                    <p>
                        This pattern (Setup -&gt; Do Work -&gt; Teardown) prevents <strong>Resource Leaks</strong>, one of the most common causes of server crashes.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The "With" Syntax Mechanics</h2>
                <p>
                    The `with` statement translates into a specific sequence of actions protected by a `finally` block.
                </p>
                <CodeBlock language="python" code={`# What you write:
with open("data.txt") as f:
    data = f.read()

# What Python actually does (roughly):
manager = open("data.txt")
f = manager.__enter__()
try:
    data = f.read()
finally:
    manager.__exit__(...)`} />
                <p>
                    Because the cleanup code is in `finally`, it is guaranteed to run, even if your code crashes, returns early, or uses `sys.exit()`. This is called <strong>Deterministic Cleanup</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>3. Creating Custom Context Managers</h2>
                <p>
                    You can make your own classes compatible with `with` by implementing two magic methods.
                </p>
                <ul className="premium-list">
                    <li>`__enter__(self)`: Setup code. Returns the object to be assigned to `as variable`.</li>
                    <li>`__exit__(self, exc_type, exc_val, exc_tb)`: Teardown code. Handles exceptions.</li>
                </ul>
                <CodeBlock language="python" code={`class TimerContext:
    def __enter__(self):
        import time
        self.start = time.time()
        return self # This becomes 't' in 'as t'

    def __exit__(self, exc_type, exc_val, exc_tb):
        import time
        self.end = time.time()
        print(f" Elapsed: {self.end - self.start:.4f}s")
        # Returning False (or None) means "Don't suppress exception"
        return False 

with TimerContext() as t:
    for i in range(1000000):
        pass
# Output:  Elapsed: 0.04s`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Suppressing Exceptions</h2>
                <p>
                    The `__exit__` method is the only place in Python where you can swallow exceptions that occurred <em>before</em> your code runs.
                    If `__exit__` returns `True`, the exception is considered "handled" and execution continues after the `with` block.
                </p>
                <CodeBlock language="python" code={`class IgnoreErrors:
    def __enter__(self):
        pass

    def __exit__(self, exc_type, exc_val, exc_tb):
        if exc_type == ZeroDivisionError:
            print("Instruction ignored: Division by zero detected.")
            return True # Swallow the error!

print("Start")
with IgnoreErrors():
    x = 1 / 0 # Normally this crashes the app
print("End") # This prints!`} />
            </div>

            <div className="content-section">
                <h2>5. The `contextlib` Utility Belt</h2>
                <p>
                    Python's `contextlib` module provides powerful tools to avoid writing full classes.
                </p>
                <h3>@contextmanager</h3>
                <p>
                    Turn any generator into a context manager. Setup code calls `yield`, teardown code runs after `yield`.
                </p>
                <CodeBlock language="python" code={`from contextlib import contextmanager

@contextmanager
def temp_file_manager(filename):
    f = open(filename, "w")
    try:
        yield f
    finally:
        f.close()
        import os
        os.remove(filename) # Auto-delete file!`} />

                <h3>contextlib.suppress</h3>
                <p>
                    A ready-made replacement for `try...except pass`.
                </p>
                <CodeBlock language="python" code={`from contextlib import suppress
import os
import { ProTip } from '../../components/LessonUI';

# Verbose
try:
    os.remove("junk.txt")
except FileNotFoundError:
    pass

# Clean
with suppress(FileNotFoundError):
    os.remove("junk.txt")`} />
            </div>

            <div className="content-section">
                <h2>6. Advanced: `ExitStack`</h2>
                <p>
                    What if you need to open a variable number of files? You can't write variable `with` statements.
                    `ExitStack` allows you to enter an arbitrary number of contexts dynamically.
                </p>
                <CodeBlock language="python" code={`from contextlib import ExitStack

filenames = ["log1.txt", "log2.txt", "log3.txt"]

with ExitStack() as stack:
    # Open all files at once
    files = [stack.enter_context(open(fname)) for fname in filenames]
    
    # Process them
    for f in files:
        print(f.read())

# All files are automatically closed here!`} />
            </div>

            <div className="content-section">
                <h2>7. Async Context Managers (`async with`)</h2>
                <p>
                    In modern Python (asyncio), we often need to wait for network resources.
                    The logic is identical, but uses `__aenter__` and `__aexit__`.
                </p>
                <CodeBlock language="python" code={`import asyncio

class AsyncDatabase:
    async def __aenter__(self):
        print("Connecting to DB...")
        await asyncio.sleep(1) # Fake network delay
        return self

    async def __aexit__(self, exc_type, exc_val, exc_tb):
        print("Closing DB...")
        await asyncio.sleep(0.5)

async def main():
    async with AsyncDatabase() as db:
        print("Querying...")

# asyncio.run(main())`} />
            </div>

        </div>
    );
};

export default PythonContextManagers;


